import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

// Approximate positions on the equirectangular world map SVG (in % of viewBox)
const OFFICES = [
  {
    code: "US",
    country: "United States",
    city: "New York",
    description: "Cross-border deal flow, investor connects and US-India trade corridor.",
    x: 27.5,
    y: 35,
    labelPosition: "below",
  },
  {
    code: "CA",
    country: "Canada",
    city: "Toronto",
    description: "Capital partnerships and India–Canada business advisory.",
    x: 26,
    y: 29,
    labelPosition: "above",
  },
  {
    code: "IN",
    country: "India",
    city: "Delhi NCR",
    description: "Head office. Capital advisory, FMCG brand house and pan-India lender network.",
    x: 71.5,
    y: 41,
    primary: true,
    labelPosition: "below",
  },
  {
    code: "AE",
    country: "United Arab Emirates",
    city: "Dubai",
    description: "Middle-East capital, family office relationships and trade finance.",
    x: 65.5,
    y: 43,
    labelPosition: "below",
  },
  {
    code: "SG",
    country: "Singapore",
    city: "Singapore",
    description: "South-East Asia capital, sovereign wealth connects and regional brand expansion.",
    x: 79,
    y: 53,
    labelPosition: "below",
  },
];

export default function Offices() {
  return (
    <section
      id="offices"
      data-testid="offices-section"
      className="bg-[#0A1F44] text-white py-24 md:py-32 cgp-grain overflow-hidden relative"
    >
      <div className="absolute -top-32 -left-32 w-[460px] h-[460px] rounded-full bg-[#C8A24A]/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-[420px] h-[420px] rounded-full bg-[#C8A24A]/5 blur-3xl pointer-events-none" />

      <div className="cgp-container relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="cgp-overline">Global Presence</span>
          <h2 className="font-serif font-light text-white mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Five offices.
            <br />
            <span className="italic text-white/65">One borderless partnership.</span>
          </h2>
          <p className="mt-7 text-white/70 text-lg leading-relaxed max-w-2xl">
            Headquartered in India, with on-the-ground partners across North
            America, the Middle East and South-East Asia — Catalyst Global
            Partners brings global capital and consumer relationships within
            reach.
          </p>
        </motion.div>

        {/* World map */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9 }}
          data-testid="offices-world-map"
          className="mt-16 relative w-full"
          style={{ aspectRatio: "950 / 620" }}
        >
          <img
            src="/assets/world-map.svg"
            alt="Global presence world map"
            className="w-full h-full object-contain opacity-30"
            style={{
              filter:
                "brightness(0) saturate(100%) invert(98%) sepia(11%) saturate(287%) hue-rotate(2deg) brightness(105%) contrast(91%)",
            }}
          />

          {/* Office pins */}
          {OFFICES.map((o, i) => (
            <div
              key={o.code}
              data-testid={`office-pin-${o.code.toLowerCase()}`}
              className="absolute"
              style={{
                left: `${o.x}%`,
                top: `${o.y}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              {/* Pulse ring */}
              <span
                className={`absolute inset-0 m-auto w-5 h-5 rounded-full ${
                  o.primary ? "bg-[#C8A24A]" : "bg-white"
                } opacity-30 animate-ping`}
                style={{ animationDelay: `${i * 0.4}s`, animationDuration: "2.4s" }}
              />
              {/* Outer halo */}
              <span
                className={`absolute inset-0 m-auto w-6 h-6 rounded-full border ${
                  o.primary ? "border-[#C8A24A]/60" : "border-white/60"
                }`}
              />
              {/* Inner dot */}
              <span
                className={`relative block w-3 h-3 rounded-full ${
                  o.primary
                    ? "bg-[#C8A24A] shadow-[0_0_18px_rgba(200,162,74,0.9)]"
                    : "bg-white shadow-[0_0_14px_rgba(255,255,255,0.7)]"
                }`}
              />
              {/* Label */}
              <div
                className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] uppercase tracking-[0.22em] font-semibold ${
                  o.primary ? "text-[#C8A24A]" : "text-white/85"
                } ${o.labelPosition === "above" ? "bottom-full mb-3" : "mt-3"}`}
              >
                {o.city}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Office cards */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
          {OFFICES.map((o, i) => (
            <motion.div
              key={o.code}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              data-testid={`office-card-${o.code.toLowerCase()}`}
              className={`p-7 border ${
                o.primary
                  ? "bg-[#C8A24A]/8 border-[#C8A24A]/40"
                  : "bg-[#051024]/60 border-white/10"
              } backdrop-blur-sm`}
            >
              <div className="flex items-center justify-between mb-5">
                <span
                  className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    o.primary
                      ? "bg-[#C8A24A] text-[#0A1F44]"
                      : "bg-[#C8A24A]/10 border border-[#C8A24A]/30 text-[#C8A24A]"
                  }`}
                >
                  <MapPin size={16} strokeWidth={1.8} />
                </span>
                <span
                  className={`text-[10px] uppercase tracking-[0.28em] font-semibold ${
                    o.primary ? "text-[#C8A24A]" : "text-white/45"
                  }`}
                >
                  {o.code}
                  {o.primary && " · HQ"}
                </span>
              </div>
              <div className="text-white/55 text-[10px] uppercase tracking-[0.24em]">
                {o.country}
              </div>
              <div className="font-serif text-2xl text-white mt-1">{o.city}</div>
              <span className="cgp-gold-rule mt-5" />
              <p className="mt-5 text-white/65 text-sm leading-relaxed">
                {o.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
